// Kho ảnh liệu trình + ảnh chữ ký trong IndexedDB (localStorage quá nhỏ cho ảnh). Sẽ thay bằng upload lên server khi có API.
import { useEffect, useState } from 'react';

const DB = 'khothi-media';
const STORE = 'files';
const cache = new Map<string, string>();
let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbp;
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const r = fn(db.transaction(STORE, mode).objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** Lưu ảnh dạng data URL */
export async function putMedia(id: string, dataUrl: string) {
  cache.set(id, dataUrl);
  await tx('readwrite', s => s.put(dataUrl, id));
}

export async function getMedia(id: string): Promise<string | null> {
  if (cache.has(id)) return cache.get(id)!;
  try {
    const v = (await tx<string | undefined>('readonly', s => s.get(id))) || null;
    if (v) cache.set(id, v);
    return v;
  } catch {
    return null;
  }
}

export async function delMedia(id: string) {
  cache.delete(id);
  await tx('readwrite', s => s.delete(id));
}

export function useMedia(id?: string | null) {
  const [src, setSrc] = useState<string | null>(id ? cache.get(id) || null : null);
  useEffect(() => {
    let alive = true;
    if (!id) { setSrc(null); return; }
    getMedia(id).then(v => { if (alive) setSrc(v); });
    return () => { alive = false; };
  }, [id]);
  return src;
}

/** Nén ảnh: cạnh dài tối đa `max` px, JPEG */
export function compressImage(file: File, max: number, quality: number): Promise<{ dataUrl: string; w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Không đọc được ảnh'));
      img.onload = () => {
        const k = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * k);
        const h = Math.round(img.height * k);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', quality), w, h });
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/** SHA-256 (hex); trình duyệt không hỗ trợ (http không bảo mật) thì dùng FNV-1a */
export async function digest(text: string): Promise<string> {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return `fnv-${(h >>> 0).toString(16)}`;
  }
}
